import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AnimalSummary, Producer } from '@homeleite/domain';
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil, finalize } from 'rxjs/operators';
import { ApplicationLog, FailLogResult, LogResult } from '../../../../../../../../libs/domain/src/lib/logs/application-log';
import { ApplicationLogsRepository } from '../../../../../../../../libs/domain/src/lib/logs/application-logs-repository';
import { EMPTY } from '@homeleite/common-utils';

export interface AnimalLogGroup {
    animal: AnimalSummary;
    logs: ApplicationLog[];
}

const PAGE_SIZE = 10;

@Component({
    selector: 'homeleite-producer-details-logs',
    templateUrl: './producer-details-logs.component.html',
    styleUrls: ['./producer-details-logs.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ProducerDetailsLogsComponent implements OnInit, OnDestroy {
    @Input() producer: Producer;

    groupedLogs: AnimalLogGroup[] = [];
    isLoading = false;
    hasMoreLogs = true;
    currentPage = 0;

    private allLogs: ApplicationLog[] = [];
    private searchSubject = new BehaviorSubject<string>(EMPTY);
    private destroy$ = new Subject<void>();

    constructor(
        private readonly repository: ApplicationLogsRepository,
        private readonly changeDetector: ChangeDetectorRef
    ) {}

    ngOnInit(): void {
        this.fetchLogs(true);

        this.searchSubject.pipe(
            debounceTime(400),
            distinctUntilChanged(),
            takeUntil(this.destroy$)
        ).subscribe(() => {
            this.fetchLogs(true);
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    fetchLogs(isNewSearch = false): void {
        if (this.isLoading) return;

        if (isNewSearch) {
            this.currentPage = 0;
            this.allLogs = [];
            this.hasMoreLogs = true;
        }

        this.isLoading = true;
        this.repository.pageProducerLogsBy(this.producer.id, this.searchSubject.value, this.currentPage, PAGE_SIZE)
            .pipe(
                finalize(() => {
                    this.isLoading = false;
                    this.changeDetector.markForCheck();
                })
            )
            .subscribe({
                next: newLogs => {
                    if (newLogs.length < PAGE_SIZE) {
                        this.hasMoreLogs = false;
                    }

                    this.allLogs.push(...newLogs);
                    this.groupedLogs = this.groupLogsByAnimal(this.allLogs);
                    this.changeDetector.markForCheck();
                },
                error: err => {
                    console.error("Erro ao buscar logs:", err);
                    this.hasMoreLogs = false;
                }
            });
    }

    onSearchChange(term: string): void {
        this.searchSubject.next(term);
    }

    loadMore(): void {
        if (this.isLoading || !this.hasMoreLogs) return;
        this.currentPage++;
        this.fetchLogs(false);
    }

    private groupLogsByAnimal(logs: ApplicationLog[]): AnimalLogGroup[] {
        const groups = new Map<string, AnimalLogGroup>();
        for (const log of logs) {
            if (!log.animal?.id) continue;
            let group = groups.get(log.animal.id);
            if (!group) {
                group = { animal: log.animal, logs: [] };
                groups.set(log.animal.id, group);
            }
            group.logs.push(log);
        }
        return Array.from(groups.values()).sort((a, b) => {
            const dateA = new Date(a.logs[0]?.result?.end || 0).getTime();
            const dateB = new Date(b.logs[0]?.result?.end || 0).getTime();
            return dateB - dateA;
        });
    }

    isFail(result: LogResult): result is FailLogResult {
        return result && result['@type'] === 'FailLogResult';
    }
}
